import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import type { Permission } from '@endcopilot/shared';
import { useSession } from './lib/session';
import { AppShell } from './layout/AppShell';
import { Alert } from './components/ui';
import { LoginPage } from './pages/LoginPage';
import { ForcedPasswordChange } from './pages/ForcedPasswordChange';
import { HomePage } from './pages/HomePage';
import { TenantsPage } from './pages/TenantsPage';
import { UsersPage } from './pages/UsersPage';
import { GroupsPage } from './pages/GroupsPage';
import { AuditPage } from './pages/AuditPage';
import { AccountPage } from './pages/AccountPage';
import { DevicesPage } from './pages/DevicesPage';
import { DeviceDetailPage } from './pages/DeviceDetailPage';
import { EnrollmentPage } from './pages/EnrollmentPage';
import { PoliciesPage } from './pages/PoliciesPage';

/** Exige empresa selecionada e a permissão; caso contrário explica o motivo. */
function Scoped({ permission, children }: { permission: Permission; children: ReactNode }) {
  const { tenant, can } = useSession();
  if (!tenant) return <Alert tone="info">Selecione uma empresa no topo da página.</Alert>;
  if (!can(permission))
    return <Alert tone="warning">Seu papel nesta empresa não permite acessar esta página.</Alert>;
  return children;
}

function SuperadminOnly({ children }: { children: ReactNode }) {
  const { me } = useSession();
  return me?.user.isSuperadmin ? children : <Navigate to="/" replace />;
}

export function App() {
  const { status, me } = useSession();

  if (status === 'loading') {
    return (
      <main className="grid min-h-dvh place-items-center bg-slate-50">
        <p className="text-sm text-slate-500">Carregando…</p>
      </main>
    );
  }
  if (status === 'anonymous') return <LoginPage />;
  if (me?.user.mustChangePassword) return <ForcedPasswordChange />;

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<HomePage />} />
        <Route
          path="empresas"
          element={
            <SuperadminOnly>
              <TenantsPage />
            </SuperadminOnly>
          }
        />
        <Route
          path="dispositivos"
          element={
            <Scoped permission="devices:read">
              <DevicesPage />
            </Scoped>
          }
        />
        <Route
          path="dispositivos/:id"
          element={
            <Scoped permission="devices:read">
              <DeviceDetailPage />
            </Scoped>
          }
        />
        <Route
          path="politicas"
          element={
            <Scoped permission="policies:read">
              <PoliciesPage />
            </Scoped>
          }
        />
        <Route
          path="ativacao"
          element={
            <Scoped permission="devices:manage">
              <EnrollmentPage />
            </Scoped>
          }
        />
        <Route
          path="usuarios"
          element={
            <Scoped permission="users:read">
              <UsersPage />
            </Scoped>
          }
        />
        <Route
          path="grupos"
          element={
            <Scoped permission="groups:read">
              <GroupsPage />
            </Scoped>
          }
        />
        <Route
          path="auditoria"
          element={
            <Scoped permission="audit:read">
              <AuditPage />
            </Scoped>
          }
        />
        <Route path="conta" element={<AccountPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
